import cn from 'classnames'
import React, { ReactElement } from 'react'
import { ExtendCurrency } from 'src/enums'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import {
  PricingPromotion,
  PricingPromotionInterval,
} from 'src/queries/organizations/pricingPlans/pricingPlans.models'

import style from './PromotionSavingsHero.style'

export type PromotionBillingCycle = 'MONTHLY' | 'YEARLY'

interface PromotionSavingsHeroProps {
  className?: string
  promotion: PricingPromotion
  billingCycle: PromotionBillingCycle
  currencyFormatter: CurrencyFormatter
}

const getInterval = (
  promotion: PricingPromotion,
  billingCycle: PromotionBillingCycle,
): PricingPromotionInterval =>
  billingCycle === 'MONTHLY'
    ? promotion.billingIntervals.monthly
    : promotion.billingIntervals.yearly

const getCurrency = (currency: string): ExtendCurrency =>
  currency === ExtendCurrency.Cad ? ExtendCurrency.Cad : ExtendCurrency.Usd

export default function PromotionSavingsHero(
  props: PromotionSavingsHeroProps,
): ReactElement {
  const {
    className = '',
    promotion,
    billingCycle,
    currencyFormatter,
  } = props
  const interval = getInterval(promotion, billingCycle)
  const currency = getCurrency(promotion.currency)
  const formatCents = (amountCents: number): string =>
    currencyFormatter({ amount: amountCents / 100, currency })

  return (
    <section
      className={cn('PromotionSavingsHero', className)}
      css={style}
      data-testid="promotion-savings-hero"
      aria-label={promotion.displayName}
    >
      <div className="PromotionSavingsHero-copy">
        <div className="PromotionSavingsHero-brand">
          <img
            src={promotion.logoUrl}
            alt=""
            className="PromotionSavingsHero-logo"
          />
          <p className="PromotionSavingsHero-eyebrow">{interval.copy.eyebrow}</p>
        </div>
        <h2 className="PromotionSavingsHero-headline">{interval.copy.headline}</h2>
        <p className="PromotionSavingsHero-description">
          {interval.copy.description}
        </p>
      </div>
      <div
        className="PromotionSavingsHero-pricing"
        data-testid="promotion-savings-hero-savings-panel"
        aria-live="polite"
        aria-atomic="true"
      >
        <p className="PromotionSavingsHero-firstYearPriceLabel">
          <span aria-hidden="true">{'★'}</span>
          {interval.firstYearPriceLabel}
        </p>
        <div className="PromotionSavingsHero-firstYearPriceSummary">
          <p className="PromotionSavingsHero-firstYearPriceAmount">
            {formatCents(interval.discountedFirstYearCents)}
          </p>
          <p className="PromotionSavingsHero-savingsDetail">
            {interval.savingsDetail}
          </p>
        </div>
        <div className="PromotionSavingsHero-divider" />
        <p
          className="PromotionSavingsHero-paymentSummary"
          data-testid="promotion-savings-hero-payment-summary"
        >
          {`${interval.priceSummaryPrefix} `}
          <span className="PromotionSavingsHero-discountedPrice">
            {formatCents(interval.discountedFirstYearCents)}
          </span>
          {` ${interval.priceSummaryComparison} `}
          <s className="PromotionSavingsHero-standardPrice">
            {formatCents(interval.standardFirstYearCents)}
          </s>
        </p>
        <p
          className="PromotionSavingsHero-freeSeats"
          data-testid="promotion-savings-hero-free-seats"
        >
          <span aria-hidden="true">{'★'}</span>
          {interval.freeSeatsLabel}
        </p>
      </div>
    </section>
  )
}
